import { storage as storageApi } from "../lib/supabase.js";
import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Worker setup — use CDN with exact version to avoid mismatches
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ─── Markdown-lite renderer ─────────────────────────────────────────────────

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} style={{ color: "#e0e0e0", fontWeight: 600 }}>{part.slice(2, -2)}</strong>
      : part
  );
}

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s*/, "")); i++; }
      elements.push(<ol key={elements.length} style={{ margin: "8px 0", paddingLeft: 20, fontSize: 13, color: "#ccc", lineHeight: 1.7 }}>{items.map((it, j) => <li key={j} style={{ marginBottom: 4 }}>{renderInline(it)}</li>)}</ol>);
      continue;
    }
    if (/^\s*[-*•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*•]\s*/, "")); i++; }
      elements.push(<ul key={elements.length} style={{ margin: "8px 0", paddingLeft: 20, fontSize: 13, color: "#ccc", lineHeight: 1.7, listStyleType: "disc" }}>{items.map((it, j) => <li key={j} style={{ marginBottom: 4 }}>{renderInline(it)}</li>)}</ul>);
      continue;
    }
    if (!line.trim()) { i++; continue; }
    elements.push(<p key={elements.length} style={{ margin: "6px 0", fontSize: 13, color: "#ccc", lineHeight: 1.7 }}>{renderInline(line)}</p>);
    i++;
  }
  return elements;
}

// ─── PDF Viewer (react-pdf) ─────────────────────────────────────────────────

function PDFViewer({ pdfSource, goToPage, color }) {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const containerRef = useRef(null);

  useEffect(() => {
    if (goToPage && goToPage > 0 && goToPage <= totalPages) setCurrentPage(goToPage);
  }, [goToPage, totalPages]);

  if (!pdfSource) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#444", fontSize: 13 }}>Loading PDF...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #1e1e2e", flexShrink: 0, background: "#0a0a10" }}>
        <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} style={{ padding: "4px 10px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 4, color: currentPage <= 1 ? "#333" : "#ccc", fontSize: 12, cursor: currentPage <= 1 ? "default" : "pointer", fontFamily: "inherit" }}>←</button>
        <span style={{ fontSize: 12, color: "#888", minWidth: 90, textAlign: "center" }}>Page {currentPage} / {totalPages}</span>
        <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} style={{ padding: "4px 10px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 4, color: currentPage >= totalPages ? "#333" : "#ccc", fontSize: 12, cursor: currentPage >= totalPages ? "default" : "pointer", fontFamily: "inherit" }}>→</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} style={{ padding: "4px 8px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 4, color: "#888", fontSize: 12, cursor: "pointer" }}>−</button>
        <span style={{ fontSize: 11, color: "#555", minWidth: 40, textAlign: "center" }}>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} style={{ padding: "4px 8px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 4, color: "#888", fontSize: 12, cursor: "pointer" }}>+</button>
      </div>

      {/* PDF Pages */}
      <div ref={containerRef} style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", padding: 16, background: "#08080c" }}>
        <Document
          file={pdfSource}
          onLoadSuccess={({ numPages }) => setTotalPages(numPages)}
          onLoadError={(err) => console.error("PDF load error:", err)}
          loading={<div style={{ color: "#555", fontSize: 13, padding: 40 }}>Loading PDF...</div>}
          error={<div style={{ color: "#f87171", fontSize: 13, padding: 40 }}>Failed to load PDF.</div>}
        >
          <Page
            pageNumber={currentPage}
            scale={scale}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </div>
    </div>
  );
}

// ─── Text Reader ────────────────────────────────────────────────────────────

function TextViewer({ rawText, color }) {
  if (!rawText) return <div style={{ padding: 40, color: "#444", fontSize: 13, textAlign: "center" }}>No text content available.</div>;

  const paragraphs = rawText.split(/\n\s*\n/).filter((p) => p.trim());
  return (
    <div style={{ padding: "24px 28px", overflowY: "auto", height: "100%", lineHeight: 1.8 }}>
      {paragraphs.map((para, i) => {
        const t = para.trim();
        const isHeading = t.length < 80 && !t.endsWith(".") && t === t.toUpperCase() && t.length > 2;
        return isHeading
          ? <h3 key={i} style={{ fontSize: 14, fontWeight: 700, color: "#e0e0e0", margin: "24px 0 8px", borderBottom: `1px solid ${color}20`, paddingBottom: 6 }}>{t}</h3>
          : <p key={i} style={{ fontSize: 13, color: "#aaa", margin: "8px 0", lineHeight: 1.8 }}>{t}</p>;
      })}
    </div>
  );
}

// ─── Chat Panel ─────────────────────────────────────────────────────────────

function ChatPanel({ account, scope, scopeLabel, onSourceClick, color, documents, getTenantClient }) {
  const platformUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // filename being downloaded
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Download PDF from tenant storage
  const handleDownload = async (source) => {
    const doc = documents?.find((d) => d.original_filename === source.filename || d.filename === source.filename);
    if (!doc?.storage_path) {
      // No storage path — can't download
      alert("PDF file not available for download. The document may have been uploaded before file storage was enabled.");
      return;
    }
    setDownloading(source.filename);
    try {
      const { data, error } = await storageApi.download(doc.storage_path);
      if (error || !data) throw new Error((typeof error === "string" ? error : error?.message) || "Download failed");

      // Trigger browser download
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.filename || "document.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      alert(`Download failed: ${err.message}`);
    }
    setDownloading(null);
  };

  useEffect(() => { setMessages([]); setInput(""); setTimeout(() => inputRef.current?.focus(), 200); }, [scope?.join?.(",")]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setMessages((p) => [...p, { role: "user", content: q }]);
    setInput(""); setLoading(true);
    try {
      const hist = messages.filter((m) => m.role === "user" || m.role === "assistant").map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${platformUrl}/functions/v1/document-qa?account=${account.slug}`, {
        method: "POST", headers: { Authorization: `Bearer ${anonKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, document_ids: scope || null, conversation_history: hist }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Q&A failed");
      setMessages((p) => [...p, { role: "assistant", content: data.answer, sources: data.sources || [], model: data.model, latency: data.latency_ms, tokens: data.tokens }]);
    } catch (err) { setMessages((p) => [...p, { role: "error", content: err.message }]); }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0b0b12" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #1e1e2e", flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#e0e0e0" }}>Document Q&A</div>
        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{scopeLabel}</div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#444" }}>
            <div style={{ fontSize: 24, marginBottom: 10, opacity: 0.3 }}>💬</div>
            <div style={{ fontSize: 12 }}>Ask questions about {scopeLabel.toLowerCase()}</div>
            <div style={{ fontSize: 11, color: "#333", marginTop: 6 }}>The AI reads the documents and synthesizes answers.</div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            {msg.role === "user" && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ maxWidth: "90%", padding: "10px 14px", background: color, borderRadius: "14px 14px 4px 14px", fontSize: 13, color: "#fff", lineHeight: 1.5 }}>{msg.content}</div>
              </div>
            )}
            {msg.role === "assistant" && (
              <div>
                <div style={{ padding: "14px 16px", background: "#111118", border: "1px solid #1a1a2a", borderRadius: "14px 14px 14px 4px" }}>{renderMarkdown(msg.content)}</div>
                {msg.sources?.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    {msg.sources.map((s, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#0a0a10", border: "1px solid #1a1a2a", borderRadius: 8 }}>
                        <span style={{ color, fontSize: 12 }}>📄</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.filename}
                            {s.section && <span style={{ color: "#555" }}> · {s.section}</span>}
                          </div>
                          <div style={{ fontSize: 10, color: "#444", marginTop: 1 }}>
                            {s.chunks && <span>{s.chunks} chunk{s.chunks > 1 ? "s" : ""}</span>}
                            {s.score && <span style={{ marginLeft: 6, color: `${color}90`, fontWeight: 700 }}>{typeof s.score === "number" ? (s.score * 100).toFixed(0) : s.score}% match</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => onSourceClick?.(s)}
                          style={{ padding: "3px 8px", background: `${color}15`, border: `1px solid ${color}25`, borderRadius: 4, color, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                          title="View in PDF reader"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDownload(s)}
                          disabled={downloading === s.filename}
                          style={{ padding: "3px 8px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 4, color: "#888", fontSize: 10, fontWeight: 600, cursor: downloading === s.filename ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: downloading === s.filename ? 0.5 : 1 }}
                          title="Download PDF"
                        >
                          {downloading === s.filename ? "..." : "⬇ PDF"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 9, color: "#333", marginTop: 4, display: "flex", gap: 8, paddingLeft: 4 }}>
                  <span>{msg.model}</span><span>{msg.latency}ms</span><span>{msg.tokens} tok</span>
                </div>
              </div>
            )}
            {msg.role === "error" && <div style={{ padding: "10px 14px", background: "#2a0a0a", border: "1px solid #991b1b", borderRadius: 8, fontSize: 12, color: "#f87171" }}>{msg.content}</div>}
          </div>
        ))}
        {loading && <div style={{ display: "flex", gap: 5, padding: "10px 0" }}>{[0, 1, 2].map((j) => <div key={j} style={{ width: 7, height: 7, borderRadius: "50%", background: color, animation: `pulse 1.2s ease-in-out ${j * 0.15}s infinite` }} />)}</div>}
        <div ref={endRef} />
      </div>

      <div style={{ padding: "10px 18px", borderTop: "1px solid #1e1e2e", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && ask()} placeholder="Ask a question..." disabled={loading} style={{ flex: 1, padding: "10px 14px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 8, color: "#ccc", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
          <button onClick={ask} disabled={loading || !input.trim()} style={{ padding: "10px 16px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading || !input.trim() ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: loading || !input.trim() ? 0.5 : 1 }}>Ask</button>
        </div>
      </div>
    </div>
  );
}

// ─── Document Workstation ───────────────────────────────────────────────────

export default function DocumentWorkstation({ open, onClose, documents, scope, scopeLabel, account, getTenantClient }) {
  const color = account?.widget_color || "#6366f1";
  const [viewMode, setViewMode] = useState("pdf");
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [goToPage, setGoToPage] = useState(null);
  const [pdfSources, setPdfSources] = useState({}); // docId -> blob URL or File

  const scopedDocs = scope
    ? documents.filter((d) => scope.includes(d.id))
    : documents.filter((d) => d.status === "indexed");
  const activeDoc = scopedDocs[activeDocIndex] || scopedDocs[0];

  // Load PDF from tenant storage
  const loadPdf = useCallback(async (doc) => {
    if (!doc || pdfSources[doc.id]) return;
    if (doc.storage_path) {
      try {
        const { data, error } = await storageApi.download(doc.storage_path);
        if (data && !error) {
          const url = URL.createObjectURL(data);
          setPdfSources((p) => ({ ...p, [doc.id]: url }));
          return;
        }
      } catch (e) { console.warn("Storage download failed:", e); }
    }
  }, [getTenantClient, pdfSources]);

  useEffect(() => {
    if (open && activeDoc && viewMode === "pdf") loadPdf(activeDoc);
  }, [open, activeDoc, viewMode, loadPdf]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => { Object.values(pdfSources).forEach((url) => { if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url); }); };
  }, []);

  const handleSourceClick = (source) => {
    const idx = scopedDocs.findIndex((d) => d.original_filename === source.filename || d.filename === source.filename);
    if (idx >= 0) { setActiveDocIndex(idx); if (source.page) { setGoToPage(source.page); setViewMode("pdf"); } }
  };

  const handleLocalFile = (file) => {
    if (!activeDoc) return;
    const url = URL.createObjectURL(file);
    setPdfSources((p) => ({ ...p, [activeDoc.id]: url }));
  };

  if (!open) return null;

  const hasPdf = activeDoc && pdfSources[activeDoc.id];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "stretch", justifyContent: "center", padding: 20 }}>
      <div style={{ display: "flex", width: "100%", maxWidth: 1400, height: "100%", background: "#0b0b12", borderRadius: 16, overflow: "hidden", border: "1px solid #1e1e2e", boxShadow: "0 16px 64px rgba(0,0,0,0.6)" }}>

        {/* Left: Document Viewer */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: "1px solid #1e1e2e", minWidth: 0 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #1e1e2e", flexShrink: 0 }}>
            <button onClick={onClose} style={{ padding: "5px 12px", background: "#111118", border: "1px solid #1e1e2e", borderRadius: 6, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>

            {scopedDocs.length > 1 ? (
              <div style={{ display: "flex", gap: 4, flex: 1, overflow: "auto" }}>
                {scopedDocs.map((doc, idx) => (
                  <button key={doc.id} onClick={() => { setActiveDocIndex(idx); setGoToPage(null); }} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", background: idx === activeDocIndex ? `${color}15` : "#0a0a10", color: idx === activeDocIndex ? color : "#666", border: `1px solid ${idx === activeDocIndex ? `${color}30` : "#1a1a2a"}` }}>
                    {doc.original_filename || doc.filename}
                  </button>
                ))}
              </div>
            ) : activeDoc && (
              <span style={{ fontSize: 13, color: "#ccc", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeDoc.original_filename || activeDoc.filename}</span>
            )}

            <div style={{ display: "flex", gap: 2, background: "#0a0a10", borderRadius: 6, border: "1px solid #1e1e2e", padding: 2 }}>
              {["pdf", "text"].map((m) => (
                <button key={m} onClick={() => setViewMode(m)} style={{ padding: "4px 12px", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: "none", background: viewMode === m ? `${color}20` : "transparent", color: viewMode === m ? color : "#555", textTransform: "uppercase" }}>{m}</button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            {viewMode === "pdf" && hasPdf && (
              <PDFViewer pdfSource={pdfSources[activeDoc.id]} goToPage={goToPage} color={color} />
            )}

            {viewMode === "pdf" && !hasPdf && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.3 }}>📄</div>
                <div style={{ fontSize: 14, color: "#888", marginBottom: 8 }}>PDF not in storage yet</div>
                <div style={{ fontSize: 12, color: "#555", marginBottom: 20, maxWidth: 320, lineHeight: 1.6 }}>
                  Drop the original PDF here to view it, or switch to Text view.
                </div>
                <div
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = color; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = "#1e1e2e"; }}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#1e1e2e"; if (e.dataTransfer.files?.[0]) handleLocalFile(e.dataTransfer.files[0]); }}
                  style={{ padding: "24px 40px", border: "2px dashed #1e1e2e", borderRadius: 12, cursor: "pointer", transition: "border-color 0.2s" }}
                >
                  <div style={{ fontSize: 12, color: "#666" }}>Drop PDF here</div>
                </div>
                <button onClick={() => setViewMode("text")} style={{ marginTop: 16, padding: "8px 20px", background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 8, color, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Switch to Text View</button>
              </div>
            )}

            {viewMode === "text" && <TextViewer rawText={activeDoc?.raw_text} color={color} />}
          </div>
        </div>

        {/* Right: Chat */}
        <div style={{ width: 420, flexShrink: 0 }}>
          <ChatPanel account={account} scope={scope} scopeLabel={scopeLabel} onSourceClick={handleSourceClick} color={color} documents={scopedDocs} getTenantClient={getTenantClient} />
        </div>
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.15; transform: scale(0.8); } 50% { opacity: 0.5; transform: scale(1.1); } }`}</style>
    </div>
  );
}
